import { useCallback, useEffect, useState } from "react";

import getMenuItemsV3, {
  GetMenuItemsV3Params,
} from "api/requests/consumerApi/getMenuItemsV3";
import searchShopCoreMenuItems, {
  Params,
} from "api/requests/consumerApi/searchShopCoreMenuItems";
import getMenuItems, { DEFAULT_TAKE_COUNT } from "api/requests/getMenuItems";
import { MenuItemCard } from "custom-types/MenuItemCard";
import { MenuItemV2 } from "custom-types/MenuItemV2";

import { FilterValues } from "components/DeliveryGateway/DeliveryProductResults/hooks/useGetMerchCampaignMenuItems";

import {
  MenuFiltersGroup,
  MenuFiltersToggle,
  Response,
} from "../types/MenuFilters";

const initialState: Response = {
  availableFilters: [],
  availableSorts: [],
  menuItems: [],
  supportedFilters: [],
  totalItems: 0,
};

const useMenuFilterSearch = (menu?: Response) => {
  const [results, setResults] = useState(menu || initialState);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(
    Math.ceil((menu?.totalItems || 0) / DEFAULT_TAKE_COUNT),
  );
  const [supportedFilters, setSupportedFilters] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect(() => {
    // This is necessary for initial menu data.
    // Without this dispensaryFilterMap is null and menu filters would not render correctly.
    if (menu) {
      setSupportedFilters(supportedFilterMap(menu?.supportedFilters || []));
      setTotalPages(Math.ceil((menu.totalItems || 0) / DEFAULT_TAKE_COUNT));
      setLoading(false);
    }
  }, [menu]);

  const supportedFilterMap = useCallback(
    (supportedFilters: string[]): { [key: string]: boolean } =>
      supportedFilters?.reduce((supportedMap, filterName) => {
        return {
          [filterName]: true,
          ...supportedMap,
        };
      }, {}),
    [],
  );

  const searchMenuItems = useCallback(
    async (slug: string, filterValues: FilterValues) => {
      setLoading(true);

      const params = {
        enableNewFilters: true,
        ...filterValues,
      };
      const response = await getMenuItems(slug, params, supportedFilters);

      // these decide what filters are included in the request
      setSupportedFilters(supportedFilterMap(response?.supportedFilters || []));
      setTotalPages(
        Math.ceil((response?.totalItems || 0) / DEFAULT_TAKE_COUNT),
      );
      setResults(response || initialState);
      setLoading(false);
    },
    [supportedFilters, setResults, setLoading, getMenuItems],
  );

  const searchShopCoreMenu = useCallback(
    async (
      lat: number,
      lon: number,
      searchParams: Params,
      loadMore?: boolean,
    ) => {
      const {
        boost,
        boost_options,
        sort,
        sortDirection,
        take = 15,
        skip,
        q,
        filters,
      } = searchParams;
      // only need to set loading state if a new filter was selected.
      // loadMore indicates the show more results was selected.
      setLoading(!loadMore);
      const {
        data: {
          metadata: { availableSorts, availableFilters, ...metadata },
          data: menuItems,
        },
      } = await searchShopCoreMenuItems(lat, lon, {
        boost,
        boost_options,
        filters: {
          ...filters,
          enableNewFilters: true,
        },
        q,
        skip,
        sort,
        sortDirection,
        take: take,
      });
      setSupportedFilters(supportedFilterMap(metadata?.supportedFilters || []));
      setResults({
        availableFilters: availableFilters as (
          | MenuFiltersGroup
          | MenuFiltersToggle
        )[],
        availableSorts,
        menuItems: loadMore
          ? [...(results.menuItems as MenuItemV2[]), ...menuItems]
          : menuItems,
        supportedFilters: metadata?.supportedFilters || [],
        totalItems: metadata?.totalCount,
      });
      setLoading(false);
    },
    [setResults, results, setLoading, getMenuItems],
  );

  const searchMenuItemsV3 = useCallback(
    async (
      lat: number,
      lon: number,
      searchParams: GetMenuItemsV3Params,
      loadMore?: boolean,
    ) => {
      const { take = 15 } = searchParams;
      // only need to set loading state if a new filter was selected.
      // loadMore indicates the show more results was selected.
      setLoading(!loadMore);
      const {
        data: {
          metadata: { availableSorts, availableFilters, ...metadata },
          data: menuItems,
        },
      } = await getMenuItemsV3(lat, lon, { ...searchParams, take: take });

      setSupportedFilters(supportedFilterMap(metadata?.supportedFilters || []));
      setResults({
        availableFilters: availableFilters as (
          | MenuFiltersGroup
          | MenuFiltersToggle
        )[],
        availableSorts,
        menuItems: loadMore
          ? [...(results.menuItems as MenuItemCard[]), ...menuItems]
          : menuItems,
        supportedFilters: metadata?.supportedFilters || [],
        totalItems: metadata?.totalCount,
      });
      setLoading(false);
    },
    [setResults, results, setLoading],
  );

  return {
    loading,
    results,
    searchMenuItems,
    searchMenuItemsV3,
    searchShopCoreMenu,
    totalPages,
  };
};

export default useMenuFilterSearch;
